import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader, ComingSoonNote } from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tsRel = (s) => {
  const d = new Date(s); const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "ahora";
  if (sec < 3600) return `hace ${Math.floor(sec / 60)} min`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)} h`;
  return d.toLocaleDateString("es", { day: "2-digit", month: "short" });
};

export default function Notifications() {
  const auth = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!auth?.user || typeof auth.user !== "object") return;
    const headers = auth.apiHeaders();
    axios.get(`${API}/notifications/seed`, { headers })
      .then(() => axios.get(`${API}/notifications`, { headers }))
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, [auth]);

  const markRead = async (n) => {
    try {
      await axios.post(`${API}/notifications/${n.id}/read`, {}, { headers: auth.apiHeaders() });
      setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
    } catch {}
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: auth.apiHeaders() });
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
      toast.success("Todas marcadas como leídas");
    } catch {}
  };

  if (!auth?.user || typeof auth.user !== "object") {
    return (
      <AppLayout>
        <PageHeader kicker="◊ Notificaciones" title="Notificaciones." subtitle="Inicia sesión para ver tus notificaciones." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        kicker="◊ Notificaciones"
        title="Notificaciones."
        subtitle="Mensajes, seguidores, likes, comentarios, compras, alertas del sistema."
        actions={
          <button onClick={markAll} data-testid="notif-mark-all" className="text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral inline-flex items-center gap-1.5">
            <CheckCheck className="w-3.5 h-3.5" /> Marcar todo leído
          </button>
        }
      />
      <section className="border hairline bg-cream-card rounded-2xl divide-y" data-testid="notif-list">
        {items.length === 0 && <p className="p-8 text-sm text-ink-mid text-center" data-testid="notif-empty">Sin notificaciones por ahora.</p>}
        {items.map((n) => (
          <button
            key={n.id}
            data-testid={`notif-${n.id}`}
            onClick={() => { markRead(n); if (n.href) window.location.href = n.href; }}
            className={`w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-cream transition ${n.read ? "" : "bg-coral/5"}`}
          >
            <div className="w-9 h-9 rounded-full bg-coral/15 text-coral flex items-center justify-center shrink-0">
              <Bell className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{n.title}</p>
              {n.body && <p className="text-xs text-ink-mid mt-0.5">{n.body}</p>}
              <p className="text-[10px] font-mono uppercase tracking-wider text-ink-mid mt-1">{tsRel(n.created_at)} · {n.kind}</p>
            </div>
            {!n.read && <span className="w-2 h-2 rounded-full bg-coral mt-2 shrink-0" />}
          </button>
        ))}
      </section>
      <ComingSoonNote feature="filtros, agrupación por tipo y push real-time" eta="próxima fase" />
    </AppLayout>
  );
}
